import { Reducer } from "react";
import Actions from "../actions";
import { EntitiesState, EntitiesData, EntityType } from "./state";
import { EntitiesLoadedAction, EntityAction, EntityErrorAction, EntitiesAction, EntityDataAction, EntityChangeLogLoadedAction } from "./types";
import { LoggedInAction } from "../session/types";
import { Action } from "redux";

const dataInitialState: EntitiesData<any> = {
    items: {},
    searchResult: [],
    isLoading: false,
    changing: undefined,
    changingHistory: [],
    changeLogs: {},
    changeLogsLoading: false,
}

const initialState: EntitiesState = {
    users: { ...dataInitialState },
    clients: { ...dataInitialState },
    professionals: { ...dataInitialState },
    appointments: { ...dataInitialState },
    reports: { ...dataInitialState },
    clientPacks: { ...dataInitialState },
    files: { ...dataInitialState },
    communications: { ...dataInitialState },
    payments: { ...dataInitialState },
    expenses: { ...dataInitialState },
    stock: { ...dataInitialState },
    changeLogs: { ...dataInitialState },
    companies: { ...dataInitialState },
    prescriptions: { ...dataInitialState },
    orders: { ...dataInitialState },
};

export const reconsilor = (state: EntitiesState): EntitiesState => ({
    users: {
        ...dataInitialState,
        items: state?.users?.items || {},
        changingHistory: state?.users?.changingHistory ?? [],
    },
    clients: {
        ...dataInitialState,
        items: state?.clients?.items || {},
        changingHistory: state?.clients?.changingHistory ?? [],
    },
    professionals: {
        ...dataInitialState,
        items: state?.professionals?.items || {},
        changingHistory: state?.professionals?.changingHistory ?? [],
    },
    appointments: {
        ...dataInitialState,
        items: state?.appointments?.items || {},
        changingHistory: state?.appointments?.changingHistory ?? [],
    },
    reports: {
        ...dataInitialState,
        items: state?.reports?.items || {},
        changingHistory: state?.reports?.changingHistory ?? [],
    },
    clientPacks: {
        ...dataInitialState,
        items: state?.clientPacks?.items || {},
        changingHistory: state?.clientPacks?.changingHistory ?? [],
    },
    files: {
        ...dataInitialState,
        items: state?.files?.items || {},
        changingHistory: state?.files?.changingHistory ?? [],
    },
    communications: {
        ...dataInitialState,
        items: state?.communications?.items || {},
        changingHistory: state?.communications?.changingHistory ?? [],
    },
    payments: {
        ...dataInitialState,
        items: state?.payments?.items || {},
        changingHistory: state?.payments?.changingHistory ?? [],
    },
    expenses: {
        ...dataInitialState,
        items: state?.expenses?.items || {},
        changingHistory: state?.expenses?.changingHistory ?? [],
    },
    stock: {
        ...dataInitialState,
        items: state?.stock?.items || {},
        changingHistory: state?.stock?.changingHistory ?? [],
    },
    changeLogs: {
        ...dataInitialState,
        items: state?.changeLogs?.items || {},
        changingHistory: state?.changeLogs?.changingHistory ?? [],
    },
    companies: {
        ...dataInitialState,
        items: state?.companies?.items || {},
        changingHistory: state?.companies?.changingHistory ?? [],
    },
    prescriptions: {
        ...dataInitialState,
        items: state?.prescriptions?.items || {},
        changingHistory: state?.prescriptions?.changingHistory ?? [],
    },
    orders: {
        ...dataInitialState,
        items: state?.orders?.items || {},
        changingHistory: state?.orders?.changingHistory ?? [],
    },
});

export const reducer: Reducer<EntitiesState | undefined, Action<Actions>> = (state = initialState, action): EntitiesState => {
    switch (action.type) {
        case Actions.LoggedOut: 
            return initialState;
        case Actions.LoggedIn:
            return handleLoggedIn(state, action as LoggedInAction)
        default:
            return handleEntityAction(state, action as EntitiesAction);
    }
}

const dataReducer: Reducer<EntitiesData<any>, EntitiesAction> = (state = dataInitialState, action): EntitiesData<any> => {
    switch (action.type) {
        case Actions.EntitiesLoading:
            return handleEntitiesLoading(state);
        case Actions.EntitiesLoaded:
            return handleEntitiesLoaded(state, action as EntitiesLoadedAction);
        case Actions.EntitiesSearchReset:
            return handleEntitiesSearchReset(state, action as EntitiesAction);
        case Actions.EntityOpened:
            return handleEntityOpened(state, action as EntityAction);
        case Actions.EntityDeleted:
            return handleEntityDeleted(state, action as EntityAction);
        case Actions.EntityCreating:
            return handleEntityCreating(state, action as EntityDataAction);
        case Actions.EntityCreatingCancel:
            return handleEntityCreatingCancel(state, action as EntityAction);
        case Actions.EntityEditing:
            return handleEntityEditing(state, action as EntityAction);
        case Actions.EntityEditionSubmitting:
            return handleEntityEditionSubmitting(state, action as EntityAction);
        case Actions.EntityEditionSubmitted:
            return handleEntityEditionSubmitted(state, action as EntityDataAction);
        case Actions.EntityEditionSubmitError:
            return handleEntityEditionSubmitError(state, action as EntityErrorAction);
        case Actions.EntityEditionCancel:
            return handleEntityEditionCancel(state);
        case Actions.EntityNewSubmitting:
            return handleEntityNewSubmitting(state)
        case Actions.EntityNewSubmitted:
            return handleEntityNewSubmitted(state, action as EntityDataAction)
        case Actions.EntityNewSubmitError:
            return handleEntityNewSubmitError(state, action as EntityErrorAction)
        case Actions.EntityChangeLogLoading:
            return handleEntityChangeLogLoading(state, action as EntityAction);
        case Actions.EntityChangeLogLoaded:
            return handleEntityChangeLogLoaded(state, action as EntityChangeLogLoadedAction);
        case Actions.EntityChangingUpdate:
            return handleEntityChangingUpdate(state, action as EntityDataAction);
        default:
            return state;
    }
};

const handleLoggedIn = (state: EntitiesState, action: LoggedInAction) => ({
    ...state,
    users: {
        ...state.users,
        items: { [action.payload.id]: action.payload }, 
    }
});

const handleEntityAction = (state: EntitiesState, action: EntitiesAction) => (!action.entityType ? state : { 
    ...state,
    [action.entityType]: dataReducer(state[action.entityType], action),
});

const handleEntitiesLoading = (data: EntitiesData<any>) : EntitiesData<any> => ({
    ...data,
    isLoading: true,
});

const handleEntitiesLoaded = (data: EntitiesData<any>, action: EntitiesLoadedAction) : EntitiesData<any> => {
    const existingItems = Object.values(data.items)
        .filter((item): item is typeof data.items[keyof typeof data.items] => !!item);

    const entitiesToRemove = Array.isArray(action.entitiesToRemove) ? action.entitiesToRemove : [];
    const entities = Array.isArray(action.entities) ? action.entities : null;

    if (!entities) {
        return {
            ...data,
            isLoading: false,
        };
    }

    const mergeProfessionalPhoto = (existing: any, incoming: any) => {
        if (!incoming) return existing;
        const merged = { ...existing, ...incoming };
        if (incoming.photo === undefined) merged.photo = existing?.photo;
        if (incoming.photoUrl === undefined) merged.photoUrl = existing?.photoUrl;
        if (incoming.photoFileId === undefined) merged.photoFileId = existing?.photoFileId;
        return merged;
    };

    /// unfortunately, we have to special case professionals here due to photo handling.
    /// it is necessary due to the reduced=true request that does not bring photo info.
    /// the goal of this change was to fix the navigation from client profile to professional profile
    const mergeLoadedEntity = (existing: any, incoming: any, entityType: EntityType) => {
        if (!incoming) return existing;
        if (entityType === 'professionals') {
            return mergeProfessionalPhoto(existing, incoming);
        }
        return incoming;
    };

    return {
        ...data,
        isLoading: false,
        items: Object.fromEntries([
            ...existingItems
                .filter(d => entities.some(e => e?.id === d.id) || !entitiesToRemove.includes(d.id))
                .map(d => action.loadType === 'ReducedDataLoad'
                    ? data.items[d.id] ?? entities.find(e => e.id === d.id)
                    : mergeLoadedEntity(d, entities.find(e => e.id === d.id), action.entityType)),
            ...entities.filter(e => !data.items[e?.id]),
        ].map(e => [e.id, e])),
        searchResult: action.loadType === 'NewResult' || action.loadType === 'ReducedDataLoad' ? [...new Set(entities.map(e => e.id))]
            : action.loadType === 'AppendResult' ? [...new Set([...data.searchResult.filter(id => !entitiesToRemove.includes(id)), ...entities.map(e => e.id)])]
            : data.searchResult,
        changing: data.changing && !data.changing.hasChanged && action.loadType !== 'ReducedDataLoad' && entities.some(e => e.id === data.changing?.value.id)
                ? {
                    ...data.changing,
                    value: mergeLoadedEntity(data.changing.value, entities.find(e => e.id === data.changing?.value.id), action.entityType),
                }
                : data.changing,
    }
}

const handleEntitiesSearchReset = (data: EntitiesData<any>, action: EntitiesAction) : EntitiesData<any> => ({
    ...data,
    isLoading: false,
    searchResult: [],
});

const handleEntityOpened = (data: EntitiesData<any>, action: EntityAction) : EntitiesData<any> => ({
    ...data,
    changingHistory: [...new Set([action.entityId, ...data.changingHistory])],
    changing: undefined,
});

const handleEntityDeleted = (data: EntitiesData<any>, action: EntityAction) : EntitiesData<any> => ({
    ...data,
    items: {
        ...data.items,
        [action.entityId]: undefined,
    },
    changing: undefined,
});

const handleEntityCreating = (data: EntitiesData<any>, action: EntityDataAction) : EntitiesData<any> => ({
    ...data,
    changing: {
        action: 'creating',
        hasChanged: false,
        saving: false,
        value: action.entity,
    },
});

const handleEntityCreatingCancel = (data: EntitiesData<any>, action: EntityAction) : EntitiesData<any> => ({
    ...data,
    changing: undefined,
});

const handleEntityEditing = (data: EntitiesData<any>, action: EntityAction) : EntitiesData<any> => ({
    ...data,
    changing: {
        action: 'editing',
        hasChanged: false,
        saving: false,
        value: data.items[action.entityId] || {},
        additionalActions: action.additionalActions,
    },
    changingHistory: [...new Set([action.entityId, ...data.changingHistory])],
});

const handleEntityEditionSubmitting = (data: EntitiesData<any>, action: EntityAction) : EntitiesData<any> => ({
    ...data,
    changing: data.changing && {
        ...data.changing,
        saving: true,
    } || undefined,
});

const handleEntityEditionSubmitted = (data: EntitiesData<any>, action: EntityDataAction) : EntitiesData<any> => ({
    ...data,
    changing: data.changing && action.keepItOpen && {
        ...data.changing,
        saving: false,
        value: action.entity,
    } || undefined,
    changingHistory: [...new Set([action.entity.id, ...data.changingHistory])],
    items: {
        ...data.items,
        [action.entity.id]: action.entity
    },
});

const handleEntityEditionSubmitError = (data: EntitiesData<any>, action: EntityErrorAction) : EntitiesData<any> => ({
    ...data,
    changing: data.changing && {
        ...data.changing,
        saving: false,
    } || undefined,
});

const handleEntityEditionCancel = (data: EntitiesData<any>) : EntitiesData<any> => ({
    ...data,
    changing: undefined,
});

const handleEntityNewSubmitting = (data: EntitiesData<any>) : EntitiesData<any> => ({
    ...data,
    changing: data.changing && {
        ...data.changing,
        saving: true,
    } || undefined,
});

const handleEntityNewSubmitted = (data: EntitiesData<any>, action: EntityDataAction) : EntitiesData<any> => ({
    ...data,
    changing: data.changing && action.keepItOpen && {
        ...data.changing,
        value: action.entity,
        saving: false,
    } || undefined,
    searchResult: [...new Set([action.entity.id].concat(data.searchResult))],
    changingHistory: [...new Set([action.entity.id, ...data.changingHistory])],
    items: {
        ...data.items,
        [action.entity.id]: action.entity,
    },
});

const handleEntityNewSubmitError = (data: EntitiesData<any>, _: EntityErrorAction) : EntitiesData<any> => ({
    ...data,
    changing: data.changing && {
        ...data.changing,
        saving: false,
    } || undefined,
});

const handleEntityChangingUpdate = (data: EntitiesData<any>, action: EntityDataAction): EntitiesData<any> => ({
    ...data,
    changing: {
        action: data.changing?.action,
        value: action.entity,
        hasChanged: true,
        saving: false,   
    },
});

const handleEntityChangeLogLoading = (data: EntitiesData<any>, _: EntityAction) => ({
    ...data,
    changeLogsLoading: true,
});

const handleEntityChangeLogLoaded = (data: EntitiesData<any>, action: EntityChangeLogLoadedAction) => ({
    ...data,
    changeLogsLoading: false,
    changeLogs: {
        ...data.changeLogs, 
        [action.entityId]: action.changeLog,
    },
});
